declare global {
  // Make Recorder available globally if loaded via script tag
  interface Window {
    Recorder: any; // Use 'any' or define more specific types
    webkitAudioContext: typeof AudioContext;
  }
}

import { getUserMedia, stopAllUserMedia } from "@/lib/userMedia";
import { useAppStore } from "./useAppStore";
import { create } from 'zustand';
import { useShallow } from "zustand/shallow";
import { getBrowserInfo } from '@/lib/browser';

const MIN_AUDIO_DURATION_MS = 500;
const MAX_AUDIO_DURATION_MS = 30000; // 30 seconds

function isSuspended (context:AudioContext): boolean {
  return context.state === 'suspended' || context.state === 'interrupted';
}

function monkeyPatchRecorder () {
  // Monkeypatch Recorder if needed (copy from original JS)
  if (window.Recorder.prototype._isPatched) return;
  console.log("Applying Recorder monkeypatch...");
  window.Recorder.prototype.encodeAudio = async function (
    audioFloat32Array: Float32Array
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Store the original callbacks
      const originalOndataavailable = this.ondataavailable;
      const originalOnstart = this.onstart;
      const originalOnstop = this.onstop;

      let encodedChunks: any[] = [];

      this.ondataavailable = (typedArray: any) => {
        encodedChunks.push(typedArray);
      };

      this.onstart = () => {
        // When recording starts, send the audio data to the encoder
        if (this.encoder && this.encoder.postMessage) {
          this.encoder.postMessage({
            command: "encode",
            buffers: [audioFloat32Array],
          });
        }
      };

      this.onstop = () => {
        // Combine all chunks into a single Blob
        const opusBlob = new Blob(encodedChunks, {
          type: "audio/ogg; codecs=opus",
        });

        // Restore original callbacks
        this.ondataavailable = originalOndataavailable;
        this.onstart = originalOnstart;
        this.onstop = originalOnstop;

        resolve(opusBlob);
      };

      // Start and immediately stop the recorder to trigger the encoding process
      this.start()
        .then(() => {
          this.stop();
        })
        .catch(reject);
    });
  };
  window.Recorder.prototype._isPatched = true;
}

// --- Zustand Store State Interface ---
interface AudioRecorderStoreState {
  // Core recorder instances
  recorder: any | null; // The Recorder.js instance
  recordingContext: AudioContext | null; // Web Audio API Context
  mediaStream: MediaStream | null; // Microphone stream
  analyserNode: AnalyserNode | null; // For audio visualization

  // Recording state
  audioChunks: Uint8Array[];
  initialized: boolean; // Has the recorder been successfully initialized?
  isInitializing: boolean; // Is recorder currently in initialization process?
  isRecording: boolean; // Is recorder currently recording?
  maxAudioTimer: NodeJS.Timeout | null; // Timer for max recording duration
  lastRecordStart: number; // Timestamp of last recording start

  // Error handling
  isCancelling: boolean; // Is recorder currently discarding recording?
  error: Error | null;

  // Actions (functions that modify the state)
  initializeRecorder: () => Promise<void>;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  cancelRecording: () => Promise<void>;
  changeMicrophoneInput: (deviceId: string) => Promise<void>;
  cleanupAudioResources: () => void;

  // Internal setters
  _setRecorder: (recorder: any) => void;
  _setRecordingContext: (context: AudioContext | null) => void;
  _setMediaStream: (stream: MediaStream | null) => void;
  _setAnalyserNode: (node: AnalyserNode | null) => void;
  _addAudioChunk: (chunk: Uint8Array) => void;
  _clearAudioChunks: () => void;
  _setInitialized: (initialized: boolean) => void;
  _setIsInitializing: (isInitializing: boolean) => void;
  _setIsRecording: (isRecording: boolean) => void;
  _setMaxAudioTimer: (timer: NodeJS.Timeout | null) => void;
  _setLastRecordStart: (timestamp: number) => void;
  _setError: (error: Error | null) => void;
  _setIsCancelling: (isCancelling: boolean) => void;
}

let scriptLoadingPromise: Promise<void> | null = null;
let setupScriptCalledOnce: boolean = false;
let deviceChangeListener: (() => void) | null = null; // Handle device changes internally
// Track if we have successfully initialized the mic in this session
let hasSessionMicPermission = false;

const loadOpusEncoderScript = () => {
  if (!scriptLoadingPromise) {
    scriptLoadingPromise = new Promise((resolve, reject) => {
      const scriptId = 'opus-encoder-script';
      if (document.getElementById(scriptId)) {
        console.log('Opus encoder script already exists.');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = "https://cdn.chotto.chat/js/opus-encdec/recorder.min.js";
      script.async = true;

      script.onload = () => {
        console.log('Opus encoder script loaded successfully!');
        monkeyPatchRecorder();
        resolve();
      };

      script.onerror = (error) => {
        console.error('Failed to load Opus encoder script:', error);
        reject(new Error('Failed to load Opus encoder script'));
      };

      document.head.appendChild(script);
    });
  }
  return scriptLoadingPromise;
};

// Zustand Store
export const useAudioRecorderStore = create<AudioRecorderStoreState>((set, get) => {
  const initialState = {
    recorder: null,
    recordingContext: null,
    mediaStream: null,
    analyserNode: null,
    audioChunks: [],
    initialized: false,
    isInitializing: true, // Start as initializing until script and recorder are ready
    isRecording: false,
    maxAudioTimer: null,
    lastRecordStart: 0,
    isCancelling: false,
    error: null,
  };

  return {
    ...initialState,

    // Internal Setter Actions
    _setRecorder: (recorder) => set({ recorder }),
    _setRecordingContext: (context) => set({ recordingContext: context }),
    _setMediaStream: (stream) => set({ mediaStream: stream }),
    _setAnalyserNode: (node) => set({ analyserNode: node }),
    _addAudioChunk: (chunk) => set((state) => ({ audioChunks: [...state.audioChunks, chunk] })),
    _clearAudioChunks: () => set({ audioChunks: [] }),
    _setInitialized: (initialized) => set({ initialized }),
    _setIsInitializing: (isInitializing) => set({ isInitializing }),
    _setIsRecording: (isRecording) => set({ isRecording }),
    _setMaxAudioTimer: (timer) => set({ maxAudioTimer: timer }),
    _setLastRecordStart: (timestamp) => set({ lastRecordStart: timestamp }),
    _setError: (error) => set({ error }),
    _setIsCancelling: (isCancelling) => set({ isCancelling }),

    // Public Actions

    cleanupAudioResources: () => {
      console.log("Cleaning up audio resources...");
      const { maxAudioTimer, recorder, mediaStream, recordingContext } = get();

      if (maxAudioTimer) {
        clearTimeout(maxAudioTimer);
        set({ maxAudioTimer: null });
      }
      if (recorder?.close) {
        console.log("CLOSE 1: Calling recorder.close()");
        if (recorder.clearStream) {
          console.log("Calling recorder.clearStream()");
          recorder.clearStream();
        }
        recorder.close();
        set({ recorder: null });
      }

      // Stop and clear all cached MediaStreams
      stopAllUserMedia(); 

      if (mediaStream) {
        set({ mediaStream: null });
        console.log("MediaStream reference nulled.");
      }
      
      if (recordingContext) {
        if (recordingContext.state !== "closed") {
          console.log("CLOSE 2: Calling recordingContext.close()");
          recordingContext.close();
        }
        set({ recordingContext: null });
      }
      // Remove device change listener
      if (deviceChangeListener) {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener);
        deviceChangeListener = null;
      }
      set({ initialized: false, isRecording: false, isInitializing: false, analyserNode: null }); // Reset relevant state
    },

    // Anytime you need to reclaim the mic, call this
    initializeRecorder: async () => {
      const {
        recorder,
        recordingContext,
        isInitializing,
        _setRecorder,
        _setRecordingContext,
        _setMediaStream,
        _setAnalyserNode,
        _setInitialized,
        _setIsInitializing,
        _setError,
        cleanupAudioResources,
        _setIsRecording,
        _setLastRecordStart,
        _clearAudioChunks,
        _setMaxAudioTimer,
        _addAudioChunk
      } = get(); // Get state and actions from store

      // Only proceed if the Recorder script is loaded
      if (
        typeof window.Recorder === "undefined" ||
        !setupScriptCalledOnce
      ) {
        console.log(
          "Recorder script not loaded yet or window.Recorder undefined."
        );
        _setIsInitializing(true);
        // So what do we do here???
        return;
      }

      if (
        recorder &&
        recordingContext &&
        recordingContext.state === "running" &&
        !isInitializing
      ) {
        console.log("Recorder already initialized and context running.");
        _setIsInitializing(false);
        return;
      }

      console.log("Attempting to initialize recorder...");
      _setIsInitializing(true);
      _setError(null); // Clear previous errors
      try {
        // Attempt to create context with preferred sample rate
        let context: AudioContext;
        try {
          context = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000,
          });
          console.log("Created new context with state:", context.state);
        } catch (e) {
          console.warn(
            "Failed to create AudioContext with 16kHz, using default.",
            e
          );
          context = new (window.AudioContext || window.webkitAudioContext)();
        }

        // Resume AudioContext if it's suspended/interrupted
        if (isSuspended(context)) {
          console.log(`AudioContext is ${context.state}, attempting to resume...`);
          try {
            // Note: iOS never resolves or rejects this if not triggered from a user event
            await context.resume();
          } catch (err:any) {
            // If an explicit error happens we need to do some cleanup:
            // - remove the spinner from record button
            _setIsInitializing(false);
            // - disable VAD mode if it was ON
            const { isVADEnabled, toggleVAD } = useAppStore.getState();
            if (isVADEnabled) {
              toggleVAD();
            }
            return;
          }
          console.log("AudioContext state after resume:", context.state);
        }
        _setRecordingContext(context);

        const { micInput, setMicInput, showQosMessage } = useAppStore.getState();

        const inputDevices = await navigator.mediaDevices.enumerateDevices();
        if (
          !inputDevices.some((inputDevice) => inputDevice.deviceId === micInput)
        ) {
          setMicInput(""); // Clear micInput if device not found
        }

        const userMediaOptions = micInput
          ? { audio: { deviceId: { exact: micInput } } }
          : { audio: true };
        const stream = await getUserMedia(userMediaOptions);
        _setMediaStream(stream);

        // Unified recovery function for mic disconnection/change
        const recoverFromMicLoss = async (reason: string) => {
          console.log(`Mic ${reason}, attempting to recover...`);
          const currentState = get();
          if (currentState.isInitializing) {
            console.log('Already initializing, skipping recovery');
            return;
          }
          
          if (currentState.isRecording) {
            await currentState.stopRecording();
          }
          cleanupAudioResources();
          setMicInput(''); // Clear to use default device
          useAppStore.getState().showQosMessage(`Microphone ${reason}, switching to default`, 2000);
          // Re-initialize with default device after brief delay
          setTimeout(() => get().initializeRecorder(), 500);
        };

        // Monitor stream tracks for disconnection (unplugged, disabled, etc)
        stream.getTracks().forEach((track) => {
          track.addEventListener('ended', () => recoverFromMicLoss('disconnected'));
        });

        // Set up device change listener for system-level changes
        const handleDeviceChange = async () => {
          const { isInitializing: currentlyInitializing } = get();
          if (currentlyInitializing) return;

          const devices = await navigator.mediaDevices.enumerateDevices();
          const currentMicInput = useAppStore.getState().micInput;
          
          // Check if current device still exists
          if (currentMicInput) {
            const currentDeviceExists = devices.some(d => d.deviceId === currentMicInput && d.kind === 'audioinput');
            if (!currentDeviceExists) {
              await recoverFromMicLoss('changed');
            }
          }
        };

        // Remove old listener if exists and add new one
        if (deviceChangeListener) {
          navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener);
        }
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
        deviceChangeListener = handleDeviceChange;

        const sourceNode = context.createMediaStreamSource(stream);

        const analyserNode = context.createAnalyser();
        analyserNode.fftSize = 64; // Small fftSize for quick visualization data
        sourceNode.connect(analyserNode);
        _setAnalyserNode(analyserNode); // Store the analyser node

        const config = {
          numberOfChannels: 1,
          encoderSampleRate: 16000, // Target Opus sample rate
          encoderFrameSize: 20,
          maxFramesPerPage: 40,
          encoderComplexity: 6,
          encoderApplication: 2048, // Voice
          originalSampleRate: context.sampleRate, // Use actual context rate
          streamPages: true,
          sourceNode: sourceNode,
        };

        const newRecorder = new window.Recorder(config);

        newRecorder.ondataavailable = (typedArray: Uint8Array) => {
          // This uses the internal _addAudioChunk setter
          _addAudioChunk(typedArray);
        };

        newRecorder.onstart = () => {
          console.log("Recording started");
          _setIsRecording(true);
          _setLastRecordStart(Date.now());
          _clearAudioChunks(); // Clear chunks for new recording

          // Start max duration timer
          const currentMaxTimer = get().maxAudioTimer; // Get current timer from state
          if (currentMaxTimer)
            clearTimeout(currentMaxTimer);
          const timer = setTimeout(
            () => get().stopRecording(), // Call the store's stopRecording action
            MAX_AUDIO_DURATION_MS
          );
          _setMaxAudioTimer(timer);
        };

        newRecorder.onstop = () => {
          console.log("Recording stopped");
          _setIsRecording(false);
          const currentMaxTimer = get().maxAudioTimer;
          if (currentMaxTimer)
            clearTimeout(currentMaxTimer);
          _setMaxAudioTimer(null);

          // Check if labelling as cancelled
          const { isCancelling } = get();
          if (isCancelling) {
            console.log("Recording cancelled, discarding chunks.");
            _clearAudioChunks();
            return;
          }

          const { lastRecordStart, audioChunks } = get();
          const duration = Date.now() - lastRecordStart;

          if (duration < MIN_AUDIO_DURATION_MS) {
            console.log(`Recording too short (${duration}ms), skipping.`);
            showQosMessage("Recording too short", 1500);
            _clearAudioChunks();
            return;
          }

          if (audioChunks.length > 0) {
            const blob = new Blob(audioChunks as any, {
              type: "audio/ogg; codecs=opus",
            });
            console.log("Final audio blob size:", blob.size);
            useAppStore.getState().sendAudio(blob);
            _clearAudioChunks();
          } else {
            console.warn("No audio chunks recorded.");
            showQosMessage("No audio data captured", 1500);
          }
        };

        await newRecorder.initialize; // Call initialize if the library requires it
        _setRecorder(newRecorder);
        _setInitialized(true);
        // Mark permission as granted for this session since we successfully initialized
        hasSessionMicPermission = true;
        console.log("Recorder initialized successfully");
      } catch (error: any) {
        console.error("Error initializing recorder:", error);
        const { showQosMessage } = useAppStore.getState();
        showQosMessage(`Mic Error: ${error.message}`, -1);
        _setError(error instanceof Error ? error : new Error(String(error)));
        _setRecorder(null);
        cleanupAudioResources(); // Clean up if initialization fails
      } finally {
        _setIsInitializing(false);
      }
    },

    startRecording: async () => {
      const { isRecording, isInitializing, recorder, recordingContext, initializeRecorder, _setIsRecording, _setError } = get();
      if (isRecording) return; // Already recording

      if (
        isInitializing ||
        !recorder ||
        !recordingContext ||
        recordingContext.state === "closed" ||
        isSuspended(recordingContext)
      ) {
        console.log("startRecording: Initializing/resuming recorder...");
        await initializeRecorder(); // Ensure initialized before starting
        const currentRecorder = get().recorder; // Get updated recorder after init
        if (!currentRecorder) {
          console.error("startRecording: Recorder failed to initialize after user gesture.");
          _setError(new Error("Recorder failed to initialize."));
          return; // Stop if init failed
        }
      }

      const currentContext = get().recordingContext; // Get latest context
      // Ensure AudioContext is running before starting to record
      if (
        currentContext &&
        isSuspended(currentContext)
      ) {
        console.log(`startRecording: AudioContext is ${currentContext.state}, attempting to resume...`);
        try {
          await currentContext.resume();
          console.log("AudioContext state after resume for recording:", currentContext.state);
        } catch (e: any) {
          console.error("Failed to resume AudioContext before recording:", e);
          useAppStore.getState().showQosMessage("Failed to activate microphone", 2000);
          _setError(e instanceof Error ? e : new Error(String(e)));
          return;
        }
      }

      const currentRecorder = get().recorder; // Get latest recorder again
      if (currentRecorder && currentRecorder.start) {
        console.log("Calling recorder.start()");
        try {
          await currentRecorder.start();
          // _setIsRecording is set in onstart callback of the recorder
        } catch (error: any) {
          console.error("Error starting recording:", error);
          useAppStore.getState().showQosMessage(`Recording Start Error: ${error.message}`, 2000);
          _setIsRecording(false); // Ensure state is correct if start fails
          _setError(error instanceof Error ? error : new Error(String(error)));
        }
      } else {
        console.error("Recorder not available or start method missing.");
        _setError(new Error("Recorder not available."));
      }
    },

    stopRecording: async () => {
      const { isRecording, isInitializing, recorder, _setIsRecording, _setError } = get();
      if (!isRecording && !isInitializing) return; // Only stop if currently recording or actively initializing (and failing)

      if (recorder?.stop) {
        console.log("Calling recorder.stop()");
        try {
          await recorder.stop();
          // _setIsRecording is set in onstop callback
        } catch (error: any) {
          console.error("Error stopping recording:", error);
          _setIsRecording(false); // Force state update if stop fails
          _setError(error instanceof Error ? error : new Error(String(error)));
        }
      } else {
        console.error("Recorder not available or stop method missing.");
        _setIsRecording(false); // Force state update
        _setError(new Error("Recorder not available to stop."));
      }
    },

    cancelRecording: async () => {
      const { isRecording, recorder, _setIsCancelling, stopRecording } = get();
      if (!isRecording) return;
      
      console.log("Cancelling recording...");
      _setIsCancelling(true);
      await stopRecording();
      _setIsCancelling(false);
    },

    changeMicrophoneInput: async (deviceId: string) => {
      const { micInput, setMicInput } = useAppStore.getState();
      const { isRecording, stopRecording, cleanupAudioResources, initializeRecorder } = get();

      if (micInput === deviceId) return; // No change

      setMicInput(deviceId); // Update micInput in useAppStore

      if (isRecording) {
        await stopRecording(); // Stop current recording if active
      }
      cleanupAudioResources(); // Clean up current resources
      await initializeRecorder(); // Re-initialize recorder with new device
    },
  };
});

// Initialization Logic (Outside the store 'create' but called once)
// Global script loading and initial setup of event listeners
// Runs only once per application lifecycle.

if (!setupScriptCalledOnce) {
  setupScriptCalledOnce = true;
  console.log("Initial audio recorder setup");

  // Load the Opus encoder script
  loadOpusEncoderScript()
    .then(() => {
      // Once script is loaded, set isInitializing to false (from store)
      useAudioRecorderStore.getState()._setIsInitializing(false);
      console.log("Opus encoder script ready.");
    })
    .catch((err) => {
      // If script fails to load, mark as initialized=false and set error
      useAudioRecorderStore.getState()._setIsInitializing(false);
      useAudioRecorderStore.getState()._setInitialized(false);
      useAudioRecorderStore.getState()._setError(err);
      console.error("Initial script load failed:", err);
    });

  // Attach global event listeners
  const initializeRecorderIfNeeded = async () => {
    if (document.hidden) {
      // Check if we're in presentation mode with PIP active - if so, don't clean up audio
      const { isPresentationMode, isPIPActive, isVADEnabled } = useAppStore.getState();
      if (isPresentationMode && (isPIPActive || isVADEnabled)) {
        console.log(`Page is hidden but PIP or VAD is active in presentation mode: Keeping mic resources active.`);
        return; // Don't clean up audio resources when PIP is active
      }
      
      console.log(`Page is hidden: Cleaning up mic resources.`);
      await useAudioRecorderStore.getState().stopRecording();
      useAudioRecorderStore.getState().cleanupAudioResources();
    } else {
      // Attempt to re-initialize recorder if it was cleaned up due to hidden state.
      console.log(`Page is visible`);

      // No need to re-init if already initialized.
      const { initialized } = useAudioRecorderStore.getState();
      if (initialized) {
        return;
      }

      // Check if we ever got permission IN THIS SESSION before auto-initializing
      if (!hasSessionMicPermission) {
        console.log('No session mic permission yet, skipping recorder initialization on visibility change.');
        return;
      }

      // We don't re-init on iOS: in Safari or WebView, it requires a user interaction,
      const { isIOS } = await getBrowserInfo();
      if (isIOS) {
        console.log('Skipping recorder initialization on visibility change on iOS.');
        // Remove the spinner from record button
        useAudioRecorderStore.getState()._setIsInitializing(false);
        const { isVADEnabled, toggleVAD } = useAppStore.getState();
        // Disable VAD mode if it was ON
        if (isVADEnabled) {
          toggleVAD();
        }
        return;
      }

      // Let's try to re-init
      const { initializeRecorder } = useAudioRecorderStore.getState();
      await initializeRecorder();
    }
  };

  // NOTE: We have to use pointerUp or click or something that's very clearly a human gesture
  // iOS Safari is really picky about what is considered a human interaction gesture
  // So pointerdown and touchstart are usually not enough to trigger the initializeRecorder
  const onPointerUp = async (ev: PointerEvent) => {
    console.log(`on pointer up (global listener)`);
    const { initialized, initializeRecorder } = useAudioRecorderStore.getState();
    const target = ev.target as HTMLElement;
    if (target.closest('#mic-button')) {
      console.log('Mic button was clicked!');
      return;
    }
    if (window.location.pathname === '/guest-login') {
      console.log('Currently on /guest-login page, skipping recorder initialization.');
      return;
    }
    // Check if we're in tutorial mode - don't initialize until tutorial grants mic permission
    const tutorialCompleted = localStorage.getItem('chotto_tutorial_completed');
    const micPermissionGranted = localStorage.getItem('chotto_mic_permission_granted');
    if (!tutorialCompleted && !micPermissionGranted) {
      console.log('Tutorial not completed and mic permission not granted yet, skipping recorder initialization.');
      return;
    }
    if (!initialized) {
      await initializeRecorder();
    }
  };

  document.addEventListener("visibilitychange", initializeRecorderIfNeeded);
  document.addEventListener("pointerup", onPointerUp); // For iOS/mobile context resume
}

// Maintain same Hooks style as before for external Components (MicButton.tsx)
export function useAudioRecorder() {
  const {
    isRecording,
    isInitializing,
    analyserNode,
    recorder,
  } = useAudioRecorderStore(
    useShallow((state) => ({
      isRecording: state.isRecording,
      isInitializing: state.isInitializing,
      analyserNode: state.analyserNode,
      recorder: state.recorder,
    }))
  );

  const { startRecording, stopRecording, cancelRecording, changeMicrophoneInput } = useAudioRecorderStore(); // Get actions

  return {
    isRecording,
    isInitializing,
    startRecording,
    stopRecording,
    cancelRecording,
    analyserNode,
    changeMicrophoneInput,
    recorder,
  };
}
